import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/pro-solid-svg-icons/faLink";
import type { MenuSection } from "@wondermaps/db/schema";
import { getTranslatedValue } from "@wondermaps/db/client";
import { findIconByName } from "../../data/fontawesome-icons";
import { analytics } from "../../lib/analytics";

export interface VisitorMenuSectionProps {
  section: MenuSection;
  onClose: () => void;
  mapSlug: string;
  /** Resolved brand color for menu icons (null = default muted) */
  brandAccentMenuIcons?: string | null;
}

export function VisitorMenuSection({ section, onClose, mapSlug, brandAccentMenuIcons }: VisitorMenuSectionProps) {
  const { t, i18n } = useTranslation("map");
  const lang = i18n.language;
  const translatedTitle = getTranslatedValue(section.title, section.titleTranslations, lang);
  // Filter out items with empty/invalid URLs and replace {slug} placeholder
  const validItems = section.items
    .filter((item) => item.url && item.url.trim() !== "")
    .map((item) => ({
      ...item,
      url: item.url.replace("{slug}", mapSlug),
    }))
    .sort((a, b) => a.sort_order - b.sort_order);

  // Don't render section if no valid items
  if (validItems.length === 0) {
    return null;
  }

  return (
    <div className="py-3 border-b" role="region" aria-label={translatedTitle}>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1 px-4">
        {translatedTitle}
      </h3>
      <ul>
        {validItems.map((item) => {
          const iconEntry = findIconByName(item.icon);
          const isInternal = item.url.startsWith("/");
          const translatedLabel = getTranslatedValue(item.label, item.labelTranslations, lang);

          // Internal route
          if (isInternal) {
            return (
              <li key={item.id}>
                <Link
                  to={item.url}
                  onClick={() => {
                    analytics.clickOutbound({
                      url: item.url,
                      label: item.label,
                      location: "more_quick_link",
                    });
                    onClose();
                  }}
                  className="flex items-center gap-3 px-4 py-3 min-h-[44px] hover:bg-muted/50 transition-colors"
                >
                  <FontAwesomeIcon
                    icon={iconEntry?.icon ?? faLink}
                    className={brandAccentMenuIcons ? "w-5 h-5" : "w-5 h-5 text-muted-foreground"}
                    style={brandAccentMenuIcons ? { color: brandAccentMenuIcons } : undefined}
                  />
                  <span className="text-sm truncate">{translatedLabel}</span>
                </Link>
              </li>
            );
          }

          // External URL
          return (
            <li key={item.id}>
              <a
                href={item.url}
                target={item.open_in_new_tab ? "_blank" : undefined}
                rel={item.open_in_new_tab ? "noopener noreferrer" : undefined}
                aria-label={item.open_in_new_tab ? t("menu.opensInNewTab", { label: translatedLabel }) : translatedLabel}
                className="flex items-center gap-3 px-4 py-3 min-h-[44px] hover:bg-muted/50 transition-colors"
                onClick={() => {
                  analytics.clickOutbound({
                    url: item.url,
                    label: item.label,
                    location: "more_quick_link",
                  });
                }}
              >
                <FontAwesomeIcon
                  icon={iconEntry?.icon ?? faLink}
                  className={brandAccentMenuIcons ? "w-5 h-5" : "w-5 h-5 text-muted-foreground"}
                  style={brandAccentMenuIcons ? { color: brandAccentMenuIcons } : undefined}
                />
                <span className="text-sm truncate">{translatedLabel}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
